import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Users, Stethoscope, Activity, AlertTriangle, Plus, ArrowRight, Baby, Search, ClipboardPlus, Calendar, Clock, MessageCircle, UserCheck, X, ShieldCheck, ScrollText } from "lucide-react";
import { ageFromBirth } from "../lib/calc";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { MiConsultorio } from "../components/MiConsultorio";
import OfflinePrepCard from "../components/OfflinePrepCard";
import WhatsAppReminders from "../components/agenda/WhatsAppReminders";

const fade = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pq, setPq] = useState("");
  const [appts, setAppts] = useState([]);
  const [queue, setQueue] = useState([]);
  const [requests, setRequests] = useState([]);
  const [compliance, setCompliance] = useState(null);

  useEffect(() => { api.get("/compliance/summary").then((r) => setCompliance(r.data)).catch(() => {}); }, []);

  const loadRequests = () => api.get("/appointment-requests").then((r) => setRequests(r.data)).catch(() => {});
  useEffect(() => { loadRequests(); }, []);
  const reqWa = (r) => {
    let d = (r.phone || "").replace(/\D/g, ""); if (!d) return null; if (d.length === 10) d = "52" + d;
    const msg = `Hola ${r.patient_name}, le contacto de ${user?.name || "su médico"} (ClinPed) sobre su solicitud de cita.` + (r.reason ? ` Motivo: ${r.reason}.` : "");
    return `https://wa.me/${d}?text=${encodeURIComponent(msg)}`;
  };
  const dismissRequest = async (id) => { try { await api.delete(`/appointment-requests/${id}`); setRequests((x) => x.filter((r) => r.request_id !== id)); } catch { /* noop */ } };

  useEffect(() => {
    const loadQueue = () => api.get("/queue").then((r) => setQueue(r.data)).catch(() => {});
    loadQueue();
    const id = setInterval(loadQueue, 20000);
    return () => clearInterval(id);
  }, []);

  const removeFromQueue = async (patientId) => {
    try { await api.delete(`/queue/${patientId}`); setQueue((q) => q.filter((x) => x.patient_id !== patientId)); } catch { /* noop */ }
  };
  const waitedMin = (iso) => { try { return Math.max(0, Math.round((Date.now() - new Date(iso)) / 60000)); } catch { return 0; } };

  useEffect(() => {
    api.get("/patients").then((r) => setPatients(r.data)).catch(() => {}).finally(() => setLoading(false));
    const loadAppts = () => {
      const now = new Date();
      const end = new Date(now.getTime() + 7 * 86400000);
      api.get("/appointments", { params: { start: now.toISOString(), end: end.toISOString() } })
        .then((r) => setAppts(r.data.filter((a) => a.status === "programada"))).catch(() => {});
    };
    loadAppts();
    window.addEventListener("clinped:appointments-updated", loadAppts);
    return () => window.removeEventListener("clinped:appointments-updated", loadAppts);
  }, []);

  const fmtAppt = (iso) => { try { return new Date(iso).toLocaleString("es-MX", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
  const within24 = (iso) => { const d = new Date(iso) - new Date(); return d > 0 && d <= 24 * 3600000; };
  const waDigits = (phone) => { if (!phone) return null; let d = (phone + "").replace(/\D/g, ""); if (!d) return null; if (d.length === 10) d = "52" + d; return d; };
  const waHref = (a) => {
    const num = waDigits(a.guardian_phone);
    if (!num) return null;
    const who = a.guardian_name ? `${a.guardian_name}, ` : "";
    const msg = `Hola ${who}le recordamos la cita de ${a.patient_name} con ${user?.name || "su médico"} en ClinPed para el ${fmtAppt(a.scheduled_at)}.` + (a.reason ? ` Motivo: ${a.reason}.` : "") + ` Si necesita reprogramar, por favor avísenos. ¡Gracias!`;
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };

  useEffect(() => {
    api.get("/patients").then((r) => setPatients(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const totalConsults = patients.reduce((a, p) => a + (p.consultation_count || 0), 0);
  const stats = [
    { label: "Pacientes", value: patients.length, icon: Users, color: "bg-brand-terracotta/10 text-brand-terracotta" },
    { label: "Consultas", value: totalConsults, icon: Activity, color: "bg-brand-sage/15 text-brand-sageDark" },
    { label: "Expedientes activos", value: patients.length, icon: Stethoscope, color: "bg-brand-sand/30 text-amber-700" },
  ];

  return (
    <div className="space-y-8">
      <motion.div {...fade} className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-brand-sage font-bold text-sm tracking-wide uppercase">Hola de nuevo</p>
          <h1 className="font-outfit text-3xl sm:text-4xl font-extrabold text-brand-ink tracking-tight">
            {user?.name || "Doctor"}
          </h1>
          <p className="text-muted-foreground mt-1">Bienvenido a tu panel de cuidado pediátrico.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="dashboard-new-consult" onClick={() => setPickerOpen(true)}
            className="flex items-center gap-2 bg-white border border-stone-200 text-brand-ink font-outfit font-semibold px-5 py-3 rounded-2xl transition-all hover:scale-[1.02] active:scale-[0.98] hover:border-brand-terracotta/40">
            <ClipboardPlus size={18} /> Nueva consulta
          </button>
          <button data-testid="dashboard-new-patient" onClick={() => navigate("/pacientes")}
            className="flex items-center gap-2 bg-brand-terracotta hover:bg-brand-terracottaDark text-white font-outfit font-semibold px-5 py-3 rounded-2xl transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-brand-terracotta/20">
            <Plus size={18} /> Nuevo paciente
          </button>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {stats.map((s, i) => (
          <motion.div key={s.label} {...fade} transition={{ delay: i * 0.07 }}
            className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(61,64,91,0.04)] border border-stone-100">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${s.color}`}>
              <s.icon size={24} />
            </div>
            <p className="font-outfit text-4xl font-extrabold text-brand-ink mt-4">{s.value}</p>
            <p className="text-muted-foreground text-sm font-semibold">{s.label}</p>
          </motion.div>
        ))}
      </div>

      <WhatsAppReminders />
      <MiConsultorio />

      <OfflinePrepCard />

      {compliance && compliance.total > 0 && (
        <motion.div {...fade} transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(61,64,91,0.04)] border border-stone-100" data-testid="compliance-card">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
            <h2 className="font-outfit text-xl font-bold text-brand-ink flex items-center gap-2"><ShieldCheck size={20} className="text-emerald-600" /> Cumplimiento NOM-004</h2>
            <button data-testid="dashboard-bitacora" onClick={() => navigate("/bitacora")}
              className="flex items-center gap-1.5 text-sm font-semibold text-brand-ink bg-muted hover:bg-stone-200 px-3.5 py-2 rounded-xl transition-all">
              <ScrollText size={16} /> Bitácora de accesos
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-center">
            <div className="sm:col-span-1 flex flex-col items-center justify-center">
              <div className="relative w-28 h-28">
                <svg viewBox="0 0 36 36" className="w-28 h-28 -rotate-90">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f1f5f9" strokeWidth="3.5" />
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke={compliance.compliant_pct >= 70 ? "#10b981" : compliance.compliant_pct >= 40 ? "#f59e0b" : "#ef4444"}
                    strokeWidth="3.5" strokeLinecap="round" strokeDasharray={`${compliance.compliant_pct} 100`} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="font-outfit text-2xl font-extrabold text-brand-ink" data-testid="compliance-pct">{compliance.compliant_pct}%</span>
                  <span className="text-[10px] text-muted-foreground">cumple</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Puntaje promedio: <b className="text-brand-ink">{compliance.avg_score}</b></p>
            </div>
            <div className="sm:col-span-3 grid grid-cols-3 gap-3">
              {[["verde", "Completas", "bg-emerald-500", "text-emerald-700 bg-emerald-50"],
                ["amarillo", "Parciales", "bg-amber-500", "text-amber-700 bg-amber-50"],
                ["rojo", "Incompletas", "bg-red-500", "text-red-700 bg-red-50"]].map(([k, label, dot, chip]) => (
                <div key={k} className={`rounded-2xl p-4 ${chip}`} data-testid={`compliance-${k}`}>
                  <div className="flex items-center gap-2 mb-1"><span className={`w-3 h-3 rounded-full ${dot}`} /><span className="text-xs font-semibold">{label}</span></div>
                  <p className="font-outfit text-3xl font-extrabold">{compliance.levels?.[k] || 0}</p>
                </div>
              ))}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">Evaluación automática (NOM-004) de {compliance.total} consulta(s) finalizada(s).</p>
        </motion.div>
      )}

      <motion.div {...fade} transition={{ delay: 0.12 }}
        className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(61,64,91,0.04)] border border-stone-100" data-testid="waiting-room-section">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-outfit text-xl font-bold text-brand-ink flex items-center gap-2"><UserCheck size={20} className="text-brand-terracotta" /> Sala de espera</h2>
          {queue.length > 0 && <span data-testid="queue-count" className="text-xs font-bold px-2.5 py-1 rounded-full bg-brand-terracotta text-white animate-pulse">{queue.length} listo{queue.length > 1 ? "s" : ""}</span>}
        </div>
        {queue.length === 0 ? (
          <div className="py-6 text-center text-muted-foreground text-sm" data-testid="queue-empty">Sin pacientes en espera. Enfermería los marcará como "listos" desde su app.</div>
        ) : (
          <div className="space-y-2.5" data-testid="waiting-queue">
            {queue.map((q) => (
              <div key={q.patient_id} data-testid={`queue-item-${q.patient_id}`}
                className="w-full flex items-center gap-3 p-3 rounded-2xl border border-brand-terracotta/30 bg-brand-terracotta/5">
                <div className="w-11 h-11 rounded-2xl bg-brand-terracotta/15 text-brand-terracotta flex items-center justify-center shrink-0 font-outfit font-bold">{q.patient_name?.[0] || "?"}</div>
                <div className="min-w-0 flex-1">
                  <p className="font-outfit font-semibold text-brand-ink truncate">{q.patient_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{q.vitals_summary ? `${q.vitals_summary} · ` : ""}esperando {waitedMin(q.ready_at)} min</p>
                </div>
                <button data-testid={`queue-start-${q.patient_id}`} onClick={() => navigate(`/pacientes/${q.patient_id}?tab=consultas`)}
                  className="flex items-center gap-1.5 text-xs font-bold text-white bg-brand-terracotta hover:bg-brand-terracottaDark px-3 py-2 rounded-xl transition-all shrink-0"><Stethoscope size={14} /> Pasar</button>
                <button data-testid={`queue-remove-${q.patient_id}`} onClick={() => removeFromQueue(q.patient_id)} title="Quitar de la sala"
                  className="text-muted-foreground hover:text-destructive p-1.5 shrink-0"><X size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      <motion.div {...fade} transition={{ delay: 0.13 }}
        className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(61,64,91,0.04)] border border-stone-100" data-testid="requests-section">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-outfit text-xl font-bold text-brand-ink flex items-center gap-2"><MessageCircle size={20} className="text-teal-600" /> Solicitudes de cita</h2>
          {requests.length > 0 && <span data-testid="requests-count" className="text-xs font-bold px-2.5 py-1 rounded-full bg-teal-500 text-white">{requests.length}</span>}
        </div>
        {requests.length === 0 ? (
          <div className="py-6 text-center text-muted-foreground text-sm" data-testid="requests-empty">Sin solicitudes nuevas. Los pacientes te contactan desde el directorio público (activa tu perfil en "Mi Perfil").</div>
        ) : (
          <div className="space-y-2.5" data-testid="requests-list">
            {requests.map((r) => (
              <div key={r.request_id} data-testid={`request-item-${r.request_id}`}
                className="w-full flex items-center gap-3 p-3 rounded-2xl border border-teal-200 bg-teal-50/40">
                <div className="w-11 h-11 rounded-2xl bg-teal-100 text-teal-700 flex items-center justify-center shrink-0 font-outfit font-bold">{r.patient_name?.[0] || "?"}</div>
                <div className="min-w-0 flex-1">
                  <p className="font-outfit font-semibold text-brand-ink truncate">{r.patient_name} · <span className="text-xs text-muted-foreground">{r.phone}</span></p>
                  <p className="text-xs text-muted-foreground truncate">{[r.reason, r.preferred_date && `Prefiere: ${r.preferred_date}`].filter(Boolean).join(" · ") || "Sin detalles"}</p>
                </div>
                {reqWa(r) && (
                  <a data-testid={`request-wa-${r.request_id}`} href={reqWa(r)} target="_blank" rel="noopener noreferrer" title="Contactar por WhatsApp"
                    className="flex items-center gap-1 text-[11px] font-bold text-white bg-[#25D366] hover:opacity-90 px-2.5 py-1.5 rounded-lg transition-all shrink-0"><MessageCircle size={14} /> WhatsApp</a>
                )}
                <button data-testid={`request-dismiss-${r.request_id}`} onClick={() => dismissRequest(r.request_id)} title="Descartar"
                  className="text-muted-foreground hover:text-destructive p-1.5 shrink-0"><X size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      <motion.div {...fade} transition={{ delay: 0.15 }}
        className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(61,64,91,0.04)] border border-stone-100" data-testid="agenda-section">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-outfit text-xl font-bold text-brand-ink flex items-center gap-2"><Calendar size={20} className="text-brand-sage" /> Próximas citas</h2>
          <span className="text-xs text-muted-foreground flex items-center gap-1"><MessageCircle size={13} /> Recuérdale al tutor por WhatsApp</span>
        </div>
        {appts.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground text-sm" data-testid="no-upcoming-appts">No hay citas en los próximos 7 días. Agéndalas desde el expediente del paciente (pestaña "Citas").</div>
        ) : (
          <div className="space-y-2.5" data-testid="upcoming-appts">
            {appts.slice(0, 8).map((a) => (
              <div key={a.appointment_id} data-testid={`dash-appt-${a.appointment_id}`}
                className={`w-full flex items-center gap-3 p-3 rounded-2xl border transition-all ${within24(a.scheduled_at) ? "border-brand-sage/40 bg-brand-sage/5" : "border-transparent hover:bg-muted/40"}`}>
                <div className="w-11 h-11 rounded-2xl bg-brand-sage/15 text-brand-sageDark flex items-center justify-center shrink-0"><Clock size={18} /></div>
                <button onClick={() => navigate(`/pacientes/${a.patient_id}?tab=citas`)} className="min-w-0 flex-1 text-left">
                  <p className="font-outfit font-semibold text-brand-ink truncate">{a.patient_name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{fmtAppt(a.scheduled_at)}{a.reason ? ` · ${a.reason}` : ""}</p>
                </button>
                {within24(a.scheduled_at) && <span className="text-[11px] font-bold px-2 py-1 rounded-full bg-brand-sage text-white shrink-0">Próx. 24 h</span>}
                {a.guardian_phone && (
                  <a data-testid={`dash-wa-${a.appointment_id}`} href={waHref(a)} target="_blank" rel="noopener noreferrer" title="Recordar por WhatsApp"
                    className="flex items-center gap-1 text-[11px] font-bold text-white bg-[#25D366] hover:opacity-90 px-2.5 py-1.5 rounded-lg transition-all shrink-0"><MessageCircle size={14} /> WhatsApp</a>
                )}
              </div>
            ))}
          </div>
        )}
      </motion.div>

      <motion.div {...fade} transition={{ delay: 0.2 }}
        className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(61,64,91,0.04)] border border-stone-100">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-outfit text-xl font-bold text-brand-ink">Pacientes recientes</h2>
          <button onClick={() => navigate("/pacientes")} className="text-brand-terracotta text-sm font-semibold flex items-center gap-1 hover:gap-2 transition-all">
            Ver todos <ArrowRight size={16} />
          </button>
        </div>
        {loading ? (
          <div className="py-10 flex justify-center"><div className="w-8 h-8 rounded-full border-[3px] border-brand-sage/30 border-t-brand-terracotta animate-spin" /></div>
        ) : patients.length === 0 ? (
          <div className="py-12 text-center">
            <div className="w-16 h-16 rounded-3xl bg-brand-terracotta/10 flex items-center justify-center mx-auto mb-4">
              <Baby className="text-brand-terracotta" size={32} />
            </div>
            <p className="text-brand-ink font-outfit font-semibold">Aún no tienes pacientes</p>
            <p className="text-muted-foreground text-sm mt-1">Crea tu primer expediente para comenzar.</p>
            <button onClick={() => navigate("/pacientes")} className="mt-4 text-brand-terracotta font-semibold text-sm">+ Crear paciente</button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {patients.slice(0, 6).map((p) => (
              <button key={p.patient_id} data-testid={`patient-card-${p.patient_id}`} onClick={() => navigate(`/pacientes/${p.patient_id}`)}
                className="flex items-center gap-3 p-3 rounded-2xl hover:bg-brand-terracotta/5 transition-all text-left border border-transparent hover:border-brand-terracotta/20">
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center font-outfit font-bold text-white ${p.sex === "F" ? "bg-brand-terracotta" : "bg-brand-sage"}`}>
                  {p.name?.[0]}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-outfit font-semibold text-brand-ink truncate">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{ageFromBirth(p.birth_date).text} · {p.consultation_count || 0} consultas</p>
                </div>
                <ArrowRight size={16} className="text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
      </motion.div>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader><DialogTitle className="font-outfit text-brand-ink">Nueva consulta · elegir expediente</DialogTitle></DialogHeader>
          <div className="relative mt-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <input data-testid="picker-search" value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Buscar paciente…"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-stone-50 border border-transparent focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-terracotta/40" />
          </div>
          <div className="max-h-72 overflow-y-auto scrollbar-thin mt-2 space-y-1">
            {patients.filter((p) => p.name.toLowerCase().includes(pq.toLowerCase())).map((p) => (
              <button key={p.patient_id} data-testid={`picker-patient-${p.patient_id}`}
                onClick={() => navigate(`/pacientes/${p.patient_id}?tab=consultas`)}
                className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-brand-terracotta/5 text-left transition-colors">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-white ${p.sex === "F" ? "bg-brand-terracotta" : "bg-brand-sage"}`}>{p.name?.[0]}</div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm text-brand-ink truncate">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{ageFromBirth(p.birth_date).text}</p>
                </div>
                <ClipboardPlus size={16} className="text-brand-terracotta" />
              </button>
            ))}
            {patients.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Crea un paciente primero.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
